import React, { forwardRef, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";
import useBreakpoint from "../../hooks/useBreakpoint";
import usePosition from "./usePosition";
import "./Select.scss";

// Fontaine M18 — Select / Combobox. Visual contract: Style Guide V2 `select-default` / `select-open`.
// One component replaces react-select and react-dropdown: single or multi, searchable, async options,
// create-inline. Laptop: popover under the trigger (same width). Phone: bottom sheet.

/**
 * @typedef {{ value: string|number, label: string, hint?: string, icon?: ReactNode, disabled?: boolean, group?: string }} Option
 *
 * @param {Option[]} [options]
 * @param {string|number|Array} [value]            single value, or array when multi
 * @param {(value, option|option[]) => void} onChange
 * @param {boolean} [multi]
 * @param {boolean} [searchable]                   text filter (always on when async or > 8 options)
 * @param {(query: string) => Promise<Option[]>} [loadOptions]   async source (debounced 250ms)
 * @param {(label: string) => void|Promise<Option>} [onCreate]   "Create “x”" row when no exact match
 * @param {boolean} [clearable]
 * @param {"md"|"lg"} [size="md"]                  40 · 44
 * @param {string} [placeholder="Select…"]
 * @param {string} [searchPlaceholder="Type to filter…"]   hint inside the search box (what can be searched)
 * @param {string} label                         accessible name (or aria-labelledby from Field)
 * @param {boolean} [defaultOpen]                  open the list on mount (a picker revealed by a click elsewhere)
 */
const Select = forwardRef(function Select(
  {
    options: staticOptions = [],
    value,
    onChange,
    multi = false,
    searchable,
    loadOptions,
    onCreate,
    clearable = false,
    size = "md",
    placeholder = "Select…",
    searchPlaceholder = "Type to filter…",
    label,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    invalid = false,
    disabled = false,
    fullWidth = false,
    emptyText = "No matches",
    defaultOpen = false,
    className = "",
    id,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const baseId = id || `ft-select-${autoId}`;
  const { isPhone } = useBreakpoint();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(defaultOpen && !disabled);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [asyncOptions, setAsyncOptions] = useState(null);
  const [loading, setLoading] = useState(false);

  const setRefs = (el) => {
    triggerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  const isAsync = typeof loadOptions === "function";
  const canSearch = searchable ?? (isAsync || staticOptions.length > 8);
  const source = useMemo(() => (isAsync ? asyncOptions || [] : staticOptions), [isAsync, asyncOptions, staticOptions]);

  // async loading, debounced
  useEffect(() => {
    if (!isAsync || !open) return undefined;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await loadOptions(query);
        if (!cancelled) setAsyncOptions(Array.isArray(res) ? res : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isAsync, loadOptions, open, query]);

  const selectedValues = useMemo(() => (multi ? (Array.isArray(value) ? value : []) : value == null || value === "" ? [] : [value]), [multi, value]);
  const allKnown = useMemo(() => {
    const map = new Map();
    for (const o of staticOptions) map.set(o.value, o);
    for (const o of asyncOptions || []) map.set(o.value, o);
    return map;
  }, [staticOptions, asyncOptions]);
  const selectedOptions = selectedValues.map((v) => allKnown.get(v) || { value: v, label: String(v) });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = !q || isAsync ? source : source.filter((o) => String(o.label).toLowerCase().includes(q) || String(o.hint || "").toLowerCase().includes(q));
    return list;
  }, [source, query, isAsync]);
  const exact = filtered.some((o) => String(o.label).toLowerCase() === query.trim().toLowerCase());
  const showCreate = !!onCreate && query.trim() && !exact;
  const rows = showCreate ? [...filtered, { value: "__create__", label: `Create “${query.trim()}”`, create: true }] : filtered;

  const { style: panelStyle } = usePosition(triggerRef, panelRef, { open: open && !isPhone, side: "bottom", matchWidth: true });

  const close = () => { setOpen(false); setQuery(""); setActive(0); triggerRef.current?.focus(); };
  const toggle = () => { if (disabled) return; setOpen((o) => !o); };

  const pick = async (opt) => {
    if (!opt || opt.disabled) return;
    if (opt.create) {
      const created = await onCreate(query.trim());
      if (created && created.value !== undefined) {
        if (multi) onChange?.([...selectedValues, created.value], [...selectedOptions, created]);
        else onChange?.(created.value, created);
      }
      close();
      return;
    }
    if (multi) {
      const has = selectedValues.includes(opt.value);
      const nextValues = has ? selectedValues.filter((v) => v !== opt.value) : [...selectedValues, opt.value];
      const nextOptions = nextValues.map((v) => allKnown.get(v) || (v === opt.value ? opt : { value: v, label: String(v) }));
      onChange?.(nextValues, nextOptions);
      setQuery("");
    } else {
      onChange?.(opt.value, opt);
      close();
    }
  };
  const remove = (v) => { const next = selectedValues.filter((x) => x !== v); onChange?.(next, next.map((x) => allKnown.get(x))); };
  const clear = (e) => { e.stopPropagation(); onChange?.(multi ? [] : null, multi ? [] : null); };

  // focus the search when opened; outside click closes
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => (canSearch ? searchRef.current : panelRef.current)?.focus(), 0);
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false); setQuery("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDoc); };
  }, [open, canSearch]);
  useEffect(() => { setActive(0); }, [query, open]);

  const onKeyDown = (e) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(rows.length - 1); }
    else if (e.key === "Enter") { e.preventDefault(); pick(rows[active]); }
    else if (e.key === "Tab") { setOpen(false); setQuery(""); }
  };

  // keep the active row in view
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`${baseId}-opt-${active}`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active, open, baseId]);

  const name = ariaLabel || label;
  const listboxId = `${baseId}-listbox`;
  const activeId = open && rows[active] ? `${baseId}-opt-${active}` : undefined;

  const listbox = (
    <>
      {canSearch ? (
        <div className="ft-select__search">
          <Icon name="Search" size="sm" />
          <input
            ref={searchRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            aria-label={name ? `Search ${name}` : "Search"}
            value={query}
            placeholder={searchPlaceholder}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {loading ? <span className="ft-select__spin" aria-hidden="true"><Icon name="Loader2" size="sm" /></span> : null}
        </div>
      ) : null}
      <ul className="ft-select__list" role="listbox" id={listboxId} aria-multiselectable={multi || undefined} aria-label={name} tabIndex={-1}>
        {rows.length === 0 ? <li className="ft-select__empty">{loading ? "Loading…" : emptyText}</li> : null}
        {rows.map((o, i) => {
          const selected = selectedValues.includes(o.value);
          const groupStart = o.group && (i === 0 || rows[i - 1].group !== o.group);
          return (
            <React.Fragment key={String(o.value)}>
              {groupStart ? <li className="ft-select__group" role="presentation">{o.group}</li> : null}
              <li
                id={`${baseId}-opt-${i}`}
                role="option"
                aria-selected={selected}
                aria-disabled={o.disabled || undefined}
                className={`ft-select__option${i === active ? " is-active" : ""}${o.create ? " ft-select__option--create" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
              >
                {o.icon ? <span className="ft-select__option__icon">{o.icon}</span> : null}
                <span className="ft-select__option__label">{o.label}</span>
                {o.hint ? <span className="ft-select__option__hint">{o.hint}</span> : null}
                {selected ? <span className="ft-select__option__check" aria-hidden="true"><Icon name="Check" size="sm" /></span> : null}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </>
  );

  const classes = ["ft-select", `ft-select--${size === "lg" ? "lg" : "md"}`, fullWidth ? "ft-select--full" : "", invalid ? "is-invalid" : "", className].filter(Boolean).join(" ");
  const hasValue = selectedValues.length > 0;

  return (
    <div className={classes} {...rest}>
      <button
        ref={setRefs}
        type="button"
        id={baseId}
        className="ft-select__trigger"
        role={canSearch ? undefined : "combobox"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={!canSearch ? activeId : undefined}
        aria-label={name}
        aria-labelledby={ariaLabelledby}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        {multi && hasValue ? (
          <span className="ft-select__chips">
            {selectedOptions.map((o) => (
              <span key={String(o.value)} className="ft-select__chip">
                {o.label}
                <span role="button" tabIndex={-1} className="ft-select__chip-x" aria-label={`Remove ${o.label}`} onClick={(e) => { e.stopPropagation(); remove(o.value); }}>
                  <Icon name="X" size="xs" />
                </span>
              </span>
            ))}
          </span>
        ) : (
          <span className={`ft-select__value${hasValue ? "" : " ft-select__placeholder"}`}>{hasValue ? selectedOptions[0].label : placeholder}</span>
        )}
        <span className="ft-select__icons">
          {clearable && hasValue && !disabled ? (
            <span role="button" tabIndex={-1} className="ft-select__clear" aria-label="Clear" onClick={clear}><Icon name="X" size="xs" /></span>
          ) : null}
          <span className="ft-select__chevron" aria-hidden="true"><Icon name="ChevronDown" size="sm" /></span>
        </span>
      </button>

      {open && typeof document !== "undefined"
        ? createPortal(
            isPhone ? (
              <div className="ft-select__scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
                <div className="ft-select__sheet" ref={panelRef} role="dialog" aria-modal="true" aria-label={name} tabIndex={-1} onKeyDown={onKeyDown}>
                  <div className="ft-select__sheet-head">
                    <span>{name || placeholder}</span>
                    <button type="button" className="ft-select__clear" aria-label="Close" onClick={close}><Icon name="X" size="sm" /></button>
                  </div>
                  {listbox}
                </div>
              </div>
            ) : (
              // Floating coordinates are computed at runtime — the one legitimate inline style.
              // eslint-disable-next-line no-restricted-syntax -- TODO(M02): runtime floating position
              <div className="ft-select__panel" ref={panelRef} style={panelStyle} tabIndex={-1} onKeyDown={onKeyDown}>
                {listbox}
              </div>
            ),
            document.body
          )
        : null}
    </div>
  );
});

export default Select;
